import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
} from '@eventbrite/social-login-facebook';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { ISocialLogInUserActionDataProps } from '../actions/social';
import {
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_GOOGLE_LOGIN,
} from '../constants';
import {
    IAllContextsProps,
    ISocialSignupActionData,
} from '../constants/interfaces';

// TODO: to be written properly in TS

export const isLoginProviderEnabled = (
    socialSSOSettings: any,
    provider: string,
) => !isEmpty(socialSSOSettings) && !isEmpty(socialSSOSettings[provider]);

//Generic function to check if user has Social Account
export const hasSocialAccount = ({
    socialSSOSettings,
    signInMethods,
    socialToCompare,
    useLookupSigninMethodSyntax = false,
}: any) => {
    if (!isUndefined(signInMethods) && !isUndefined(socialToCompare)) {
        const lookupSigninMethodSyntax = useLookupSigninMethodSyntax
            ? '_login'
            : '';
        const signInMethodsToLower = signInMethods.map((v: any) =>
            v.toLowerCase(),
        );
        const socialToCompareToLower = socialToCompare.map((v: any) =>
            v.toLowerCase(),
        );
        let result = true;
        socialToCompareToLower.forEach((methodTC: any) => {
            // Here we check if socialMethod is enabled and it's included in user login methods.
            const checkSocialEnabledAndIncluded =
                (isLoginProviderEnabled(socialSSOSettings, methodTC) &&
                    !signInMethodsToLower.includes(
                        methodTC + lookupSigninMethodSyntax,
                    )) ||
                !isLoginProviderEnabled(socialSSOSettings, methodTC);

            if (result && checkSocialEnabledAndIncluded) {
                result = false;
            }
        });
        return result;
    } else {
        return false;
    }
};

/**
 * Returns an object out of the Social Login Data recieved
 * @param {object} arguments Social Login Data
 * @returns
 */
export const getSocialLogInFormData = ({
    formData,
    contexts,
}: {
    formData: ISocialLogInUserActionDataProps;
    contexts: IAllContextsProps;
}) => {
    const { accessToken, sourceProvider, skipCreation } = formData;
    const tosSocialAccepted = contexts.userContext?.state.tosSocialAccepted;
    let { forward, referrer, pckg } = contexts.configContext ?? {};

    // We need to remove the referrer from the local storage, so it won't cause issues on future logins.
    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer') ?? '';
        localStorage.removeItem('referrer');
    }

    const socialData: { [x: string]: any } = {
        accessToken,
        sourceProvider,
        skipCreation,
        forward,
        referrer: decodeURIComponent(referrer ?? ''),
        pckg,
    };

    // Only add this extra parameter when user accept ToS by Popup in new design.
    if (tosSocialAccepted) {
        socialData['accepted_tos_by_popup'] = 1;
    }

    return socialData;
};

/**
 * Returns an object out of the Social Signup Data
 */
export const getSocialSignUpFormData = ({
    formData,
    contexts,
}: {
    formData: ISocialSignupActionData;
    contexts: IAllContextsProps;
}) => {
    const { email, firstName, lastName } = formData;
    const skipCreation = false;
    const { socialProvider = '', socialData } =
        contexts.userContext?.state ?? {};
    const { forward, referrer, pckg } = contexts.configContext ?? {};

    const accessToken = socialData[socialProvider]?.accessToken;

    return {
        accessToken,
        sourceProvider: socialProvider,
        skipCreation,
        email,
        firstName,
        lastName,
        forward,
        referrer: decodeURIComponent(referrer ?? ''),
        pckg,
    };
};

/**
 * Checks if the email of the user is assosiated with a Facebook account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasFbAccount = ({
    socialSSOSettings,
    signInMethods,
}: {
    socialSSOSettings: any;
    signInMethods: string[];
}) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    ) && signInMethods.includes(SIGN_IN_METHOD_FACEBOOK_LOGIN);

/**
 * Checks if the email of the user is assosiated with an Apple account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasAppleAccount = ({
    socialSSOSettings,
    signInMethods,
}: {
    socialSSOSettings: any;
    signInMethods: string[];
}) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    ) && signInMethods.includes(SIGN_IN_METHOD_APPLE_LOGIN);

/**
 * Checks if the email of the user is assosiated with a Google account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasGoogleAccount = ({
    socialSSOSettings,
    signInMethods,
}: {
    socialSSOSettings: any;
    signInMethods: string[];
}) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
    ) && signInMethods.includes(SIGN_IN_METHOD_GOOGLE_LOGIN);

export const socialProviderCheckers: { [key: string]: any } = {
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE]: hasAppleAccount,
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK]: hasFbAccount,
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE]: hasGoogleAccount,
};
